import barba from '@barba/core';
import LocomotiveScroll from 'locomotive-scroll';
import SplitScreen from './SplitScreen';
import Navigation from './Navigation';
import Gallery from './Gallery';
import { store } from '../store';
import gsap from 'gsap';
import imagesLoaded from 'imagesloaded';

const PRIMARY_EMAIL = 'studio@andrewzuckerman.com';
const NAV_SCROLL_THRESHOLD = 50;
const PRELOADER_MAX_WAIT = 1000;
// Must stay in step with $mobile in _variables.scss.
const SCROLL_BREAKPOINT = 680;

// Viewports where a hero video is framed by its slide rather than cropped to
// fill it. Must stay in step with the matching media query in _home.scss:
// phones at any orientation, plus tablets held in portrait.
const NARROW_HERO = `(max-width: ${SCROLL_BREAKPOINT}px), (max-width: 1024px) and (orientation: portrait)`;

// Most hero videos decoding at once under NARROW_HERO, where several short
// slides share the screen. The homepage used to run four concurrently and it
// was the heaviest thing on the site; two keeps the stack alive without
// going back to that.
const MAX_HERO_PLAYING = 2;

// How long a hero poster stays up when the player never reports playback.
const POSTER_FALLBACK_REVEAL = 4000;

class Site {
    constructor() {
        this.body = document.querySelector('body');
        this.nav = document.querySelector('.navigation');
        this.menuButton = document.querySelector('.btn-menu');
        this.preloader = document.querySelector('.preloader');
        this.leftPanel = document.querySelector('.gallery__leftPanel');
        this.homeSlide = document.querySelector('.homeslider');

        // Per-page teardown handles, populated by initPage()
        this.pageTeardown = [];
        this.navigationVisible = null;
        this.resizeFrame = null;

        this.setHomeSlideHeight();

        this.scroll = this.createScroll();
        this.scrollContext = this.getScrollContext();

        // The navigation lives in the layout, outside the Barba container, so it
        // is never swapped out. Build it once — rebuilding it per page stacked a
        // fresh set of listeners on the same DOM nodes on every navigation.
        this.navigation = new Navigation({ scroll: this.scroll });

        this.storeUnsubscribe = store.subscribe(() => {
          this.navigation && this.navigation.onChange(store.getState().app);
        });

        this.setupWindowEvents();

        // Routing must be wired up last: barba.init() fires the `afterEnter`
        // view hook synchronously, which touches scroll and navigation.
        this.setupRouting();

        this.initPage();
    }

    createScroll() {
      return new LocomotiveScroll({
        el: document.querySelector('[data-scroll-container]'),
        smooth: true,
        smartphone: { smooth: false },
        // Locomotive defaults this to 1024, which left everything between
        // 680px and 1023px in native scroll mode while the stylesheet still
        // treated it as desktop.
        tablet: { smooth: true, breakpoint: SCROLL_BREAKPOINT }
      });
    }

    /**
     * Locomotive's `destroy()` drops its classes and listeners but leaves
     * every inline style it wrote in place, and nothing in native mode ever
     * writes — or clears — them again. Two kinds matter after a rebuild:
     *
     * - `opacity: 0` and `pointer-events: none`, which smooth mode puts on any
     *   section scrolled out of view (see its detectElements). Resize past the
     *   breakpoint while the hero is off screen and the hero stays invisible —
     *   a blank white band, with the markup perfectly correct — until a reload.
     * - the `matrix3d` transforms on sections and on the sticky and parallax
     *   elements inside them, which otherwise leave content translated up the
     *   page and outside the viewport.
     *
     * The inview attribute and `is-inview` go too, so the new instance decides
     * visibility from scratch rather than inheriting the old instance's view.
     */
    clearScrollStyles() {
      document.querySelectorAll('[data-scroll-section]').forEach((section) => {
        section.style.removeProperty('opacity');
        section.style.removeProperty('pointer-events');
        section.removeAttribute('data-scroll-section-inview');
      });

      document.querySelectorAll('[data-scroll-section], [data-scroll]').forEach((el) => {
        el.style.removeProperty('transform');
        el.style.removeProperty('-webkit-transform');
        el.classList.remove('is-inview');
      });
    }

    /**
     * Mirrors Locomotive's own context detection so we can tell when it would
     * pick a different scroll mode than the one currently running.
     */
    getScrollContext() {
      const touchUA = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isMobile = touchUA || window.innerWidth < SCROLL_BREAKPOINT;
      const isTablet = isMobile && window.innerWidth >= SCROLL_BREAKPOINT;

      if (!isMobile) return 'desktop';
      return isTablet ? 'tablet' : 'smartphone';
    }

    // smooth: true (desktop), tablet.smooth: true, smartphone.smooth: false
    isSmoothContext(context) {
      return context !== 'smartphone';
    }

    /**
     * Locomotive picks smooth-vs-native once, in its constructor, and only
     * re-checks from the *smooth* core's resize handler — so an instance that
     * booted in native mode stays native however wide the window gets. The
     * desktop layout depends on smooth mode (sticky panels, parallax), so
     * crossing the breakpoint has to rebuild the instance ourselves. The
     * library's own `reloadOnContextChange` is no help here: it is never
     * reached from native mode, and its remedy is a full page reload.
     */
    rebuildScroll() {
      this.destroyPage();
      this.scroll.destroy();
      this.clearScrollStyles();

      this.scroll = this.createScroll();
      this.navigation.scroll = this.scroll;

      this.initPage();
      this.scroll.update();

      // destroy() does not cancel every frame the old instance had queued, so
      // one can land after the cleanup above and write its styles back. Clear
      // once more on the next frame — only when the new instance is native,
      // since a smooth one legitimately writes styles of its own.
      if (!this.isSmoothContext(this.scrollContext)) {
        window.requestAnimationFrame(() => {
          if (!this.isSmoothContext(this.getScrollContext())) this.clearScrollStyles();
        });
      }

      // SplitScreen writes panel widths as inline px. Re-measure now so the
      // values left over from the previous layout don't survive the rebuild.
      this.splitScreen && this.splitScreen.update();
      this.gallery && this.gallery.update();
    }

    setupRouting() {
      const _self = this;

      barba.init({
          schema: {
              prefix: 'data-site',
              wrapper: 'app'
          },
          transitions: [{
            name: 'slide',
            leave() {
              document.querySelector('.site-wrapper').classList.add('is-leaving');

              return gsap.to('.preloader', {
                right: 0
              });
            },
            afterLeave() {
              return gsap.to('.preloader', {
                left: '100vw',
                delay: .5
              });
            }
          }],
          views: [{
            namespace: 'site',
            beforeLeave() {
              _self.gallery && _self.gallery.closeGallery();
              _self.navigation && _self.navigation.hideGalleryMenu();
              if (_self.nav) _self.nav.style.pointerEvents = 'none';
              _self.destroyPage();
            },
            afterEnter() {
              _self.scroll.update();
              _self.scroll.scrollTo('top', { duration: 0, disableLerp: true });
              _self.navigation.reset();
              document.querySelector('.site-wrapper').classList.remove('is-leaving');
              if (_self.nav) _self.nav.style.pointerEvents = 'all';
            }
          }]
      });

      barba.hooks.before(() => {
        document.querySelector('.site-wrapper').classList.add('is-leaving');
        this.scroll.scrollTo('top', { duration: 10 });
      });

      barba.hooks.after(() => {
        this.initPage();
        this.scroll.update();
        document.querySelector('.site-wrapper').classList.remove('is-leaving');
        this.body.scrollTo(0, 1);
      });
    }

    /**
     * The first hero slide is pinned to the real viewport height, because
     * `100vh` on mobile browsers measures the window with the URL bar hidden
     * and leaves the slide taller than what is actually on screen.
     *
     * The exception is a video slide under NARROW_HERO: there the slide takes
     * the video's own aspect ratio so the whole frame shows (see _home.scss),
     * and forcing the viewport height back on would restore the crop that
     * replaced. The property is removed rather than left alone, since the
     * viewport can cross the breakpoint after the height was set.
     */
    setHomeSlideHeight() {
      if (!this.homeSlide) return;

      const framed = this.homeSlide.classList.contains('introSlide--video')
        && window.matchMedia(NARROW_HERO).matches;

      if (framed) {
        this.homeSlide.style.removeProperty('height');
        return;
      }

      this.homeSlide.style.height = window.innerHeight + 'px';
    }

    destroyPage() {
      this.pageTeardown.forEach((fn) => {
        try {
          fn();
        } catch (err) {
          console.error('Error during page teardown', err);
        }
      });
      this.pageTeardown = [];

      this.splitScreen = null;
      this.gallery = null;
      this.navigation && this.navigation.attachGallery(null);
    }

    replaceEmail() {
      const emailHolders = document.querySelectorAll('.__cf_email__');

      emailHolders.forEach((email) => {
        const parentNode = email.parentNode;
        parentNode.setAttribute("href", `mailto:${PRIMARY_EMAIL}`);
        email.innerHTML = PRIMARY_EMAIL;
      });
    }

    initAudioToggle() {
      const audioToggle = document.querySelector('#audio-toggle');
      const videoIframe = document.querySelector('#video-iframe');

      if (!audioToggle || !videoIframe) return;

      let isMuted = true; // Video starts muted
      let player = null;

      const createPlayer = () => {
        if (window.Vimeo && window.Vimeo.Player) {
          player = new window.Vimeo.Player(videoIframe);
        }
      };

      const initPlayer = () => {
        if (window.Vimeo && window.Vimeo.Player) {
          createPlayer();
          return;
        }

        // Load the Vimeo Player API once, and reuse it across navigations.
        let script = document.querySelector('script[data-vimeo-api]');

        if (!script) {
          script = document.createElement('script');
          script.src = 'https://player.vimeo.com/api/player.js';
          script.setAttribute('data-vimeo-api', '');
          document.head.appendChild(script);
        }

        script.addEventListener('load', createPlayer, { once: true });
      };

      videoIframe.addEventListener('load', initPlayer);

      // If the iframe already loaded before this ran, initialise immediately.
      if (videoIframe.contentDocument && videoIframe.contentDocument.readyState === 'complete') {
        initPlayer();
      }

      audioToggle.classList.remove('hidden');

      audioToggle.addEventListener('click', () => {
        isMuted = !isMuted;

        if (isMuted) {
          audioToggle.classList.remove('unmuted');
          audioToggle.setAttribute('aria-label', 'Unmute video audio');
        } else {
          audioToggle.classList.add('unmuted');
          audioToggle.setAttribute('aria-label', 'Mute video audio');
        }

        // Use the Vimeo Player API to toggle mute without reloading the iframe.
        if (player) {
          player.setMuted(isMuted).catch(() => {});
        } else {
          const currentSrc = videoIframe.src;

          videoIframe.src = isMuted
            ? currentSrc.replace(/muted=0/, 'muted=1')
            : currentSrc.replace(/muted=1/, 'muted=0');
        }
      });

      audioToggle.setAttribute('aria-label', 'Unmute video audio');
    }

    initVideoPassthrough() {
      const overlay = document.querySelector('.videoPassthrough');
      const iframe = document.querySelector('.embed-container iframe');
      if (!iframe) return;

      // Remove the overlay div — we no longer need it.
      if (overlay) overlay.remove();

      // Prevent the iframe from capturing scroll events by disabling
      // pointer events, then re-enable on hover so the user can click play.
      iframe.style.pointerEvents = 'none';

      const wrapper = document.querySelector('.video-wrapp');
      if (!wrapper) return;

      let hoverTimeout;
      wrapper.addEventListener('mouseenter', () => {
        // Short delay to distinguish intentional hover from scroll-through
        hoverTimeout = setTimeout(() => {
          iframe.style.pointerEvents = '';
        }, 100);
      });

      wrapper.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
        iframe.style.pointerEvents = 'none';
      });

      // For touch devices, enable on first tap
      wrapper.addEventListener('touchstart', function handler() {
        iframe.style.pointerEvents = '';
        wrapper.removeEventListener('touchstart', handler);
      }, { passive: true });

      this.pageTeardown.push(() => clearTimeout(hoverTimeout));
    }

    /**
     * The homepage hero is a stack of sticky, autoplaying Vimeo background
     * slides. All of them used to load and play at once — four concurrent video
     * decodes competing with the scroll loop for the main thread. Now only the
     * current slide (and the next, so it is ready before it is revealed) is
     * loaded and playing.
     *
     * Which slide is showing is derived from scroll position rather than an
     * IntersectionObserver: Locomotive's `data-scroll-sticky` pins elements with
     * `position: fixed`, so an observer would report every slide as permanently
     * intersecting. The slides are offset one viewport apart
     * (`data-scroll-offset` 0%, 100%, 200%…), so scrollY / viewportHeight gives
     * the active index directly.
     */
    initHeroVideos() {
      const hero = document.querySelector('#intro');
      if (!hero) return;

      const frames = [...hero.querySelectorAll('.homeslider iframe')];
      if (frames.length < 2) return;

      const command = (frame, method, value) => {
        if (!frame.getAttribute('src') || !frame.contentWindow) return;
        try {
          frame.contentWindow.postMessage(
            JSON.stringify(value === undefined ? { method } : { method, value }),
            'https://player.vimeo.com'
          );
        } catch (err) {
          // Player not ready yet; the load handler below re-applies state.
        }
      };

      /**
       * The poster covers the iframe until the video is genuinely playing —
       * a Vimeo iframe is opaque black while it boots, so the slide would
       * otherwise flash black on a slow connection. Driven by the player's own
       * `play` event rather than the iframe's load, which fires well before
       * the first frame is painted. If autoplay is refused the poster stays,
       * which is the behaviour we want anyway.
       */
      const revealVideo = (frame) => {
        const slide = frame && frame.closest('.introSlide');
        if (slide) slide.classList.add('is-video-playing');
      };

      const subscribe = (frame) => {
        command(frame, 'addEventListener', 'play');
        command(frame, 'addEventListener', 'timeupdate');
        command(frame, 'addEventListener', 'error');
      };

      const onPlayerMessage = (event) => {
        if (event.origin !== 'https://player.vimeo.com') return;

        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (err) {
            return;
          }
        }
        if (!data || !data.event) return;

        const frame = frames.find((candidate) => candidate.contentWindow === event.source);
        if (!frame) return;

        // The player announces itself when it is ready to take commands. That
        // is the reliable moment to subscribe — the iframe's own load event can
        // fire before this code runs at all, which is exactly what happens to
        // the first slide, since it carries its src in the markup.
        if (data.event === 'ready') {
          subscribe(frame);
          return;
        }

        // A refused embed — privacy extensions, a network that blocks Vimeo,
        // a video pulled from the account — renders a black error card. Keep
        // the poster over it: a still frame of the work is a better failure
        // than Vimeo's chrome, and the fallback below would otherwise reveal
        // the card a few seconds in.
        if (data.event === 'error') {
          clearTimeout(revealTimers.get(frame));
          const errored = frame.closest('.introSlide');
          if (errored) errored.classList.remove('is-video-playing');
          return;
        }

        if (data.event === 'play' || data.event === 'timeupdate') revealVideo(frame);
      };

      window.addEventListener('message', onPlayerMessage);
      this.pageTeardown.push(() => window.removeEventListener('message', onPlayerMessage));

      // Any player already loaded before this ran will never send `ready`.
      frames.forEach((frame) => frame.getAttribute('src') && subscribe(frame));

      const revealTimers = new Map();

      const load = (frame) => {
        if (!frame) return;

        // Paint the poster first so the slide is never blank while the player
        // boots. Only the first slide carries an inline background.
        const poster = frame.parentNode && frame.parentNode.querySelector('.hero-poster');
        if (poster && poster.dataset.poster) {
          poster.style.backgroundImage = `url(${poster.dataset.poster})`;
          delete poster.dataset.poster;
        }

        if (!frame.getAttribute('src')) {
          const src = frame.dataset.src;
          if (!src) return;
          frame.setAttribute('src', src);
        }

        // Safety net: if the player never reports playback — an event dropped,
        // a protocol change, autoplay allowed but silent — the poster would sit
        // over a playing video forever. Uncover it anyway after a beat. The
        // poster is the same frame the video opens on, so the swap is invisible
        // either way.
        //
        // This has to cover the first slide too, which carries its src in the
        // markup: its player is often ready before any of this runs, so it is
        // the one most likely to miss the events and keep its poster up.
        if (revealTimers.has(frame)) return;

        const reveal = setTimeout(() => revealVideo(frame), POSTER_FALLBACK_REVEAL);
        revealTimers.set(frame, reveal);
        this.pageTeardown.push(() => clearTimeout(reveal));
      };

      // Which slides are playing. Always one on desktop, where a slide fills
      // the viewport. Under NARROW_HERO a slide is only as tall as its video,
      // so several share the screen at once and a frozen one next to a playing
      // one reads as broken — hence a set rather than a single index, capped
      // so a phone is never decoding more than MAX_HERO_PLAYING videos.
      let playing = [];

      const setActive = (indices) => {
        const unchanged = indices.length === playing.length
          && indices.every((index, n) => index === playing[n]);
        if (unchanged) return;
        playing = indices;

        const wanted = new Set(indices);

        frames.forEach((frame, i) => {
          if (wanted.has(i)) {
            load(frame);
            command(frame, 'play');
          } else {
            command(frame, 'pause');
          }
        });

        // Warm the slide after the last one playing so it does not flash black
        // when it is revealed.
        if (indices.length) load(frames[Math.max.apply(null, indices) + 1]);
      };

      // Vimeo drops commands sent before the player is ready, so re-apply once
      // each iframe has actually loaded.
      frames.forEach((frame, i) => {
        const onLoad = () => {
          // Subscribe before issuing playback state: Vimeo drops both if they
          // arrive before the player is ready, and this is the first moment it
          // is listening.
          subscribe(frame);
          command(frame, playing.indexOf(i) !== -1 ? 'play' : 'pause');
        };
        frame.addEventListener('load', onLoad);
        this.pageTeardown.push(() => frame.removeEventListener('load', onLoad));
      });

      // Under NARROW_HERO a video slide is only as tall as its own frame, not
      // a viewport, so scroll position no longer maps to an index. Work from
      // the slides' own boxes instead: whatever is on screen plays, nearest
      // the middle first. Measured from the DOM, so it also survives the
      // transforms Locomotive puts on a sticky slide at tablet widths.
      const slides = frames.map((frame) => frame.closest('.introSlide'));

      // Distance is measured on both axes so this serves either layout: in the
      // carousel every slide shares a vertical centre and the horizontal term
      // decides, and where the hero stacks it is the other way round.
      const visibleIndices = () => {
        const middleY = window.innerHeight / 2;
        const middleX = window.innerWidth / 2;

        return slides
          .map((slide, i) => {
            if (!slide) return null;
            const rect = slide.getBoundingClientRect();
            const onScreen = rect.bottom > 0 && rect.top < window.innerHeight
              && rect.right > 0 && rect.left < window.innerWidth;
            if (!onScreen) return null;
            return {
              index: i,
              distance: Math.abs(rect.top + rect.height / 2 - middleY)
                + Math.abs(rect.left + rect.width / 2 - middleX)
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.distance - b.distance)
          .slice(0, MAX_HERO_PLAYING)
          .map((entry) => entry.index)
          .sort((a, b) => a - b);
      };

      const onHeroScroll = (args) => {
        if (window.matchMedia(NARROW_HERO).matches) {
          setActive(visibleIndices());
          return;
        }

        const viewport = window.innerHeight || 1;
        const index = Math.min(
          frames.length - 1,
          Math.max(0, Math.round(args.scroll.y / viewport))
        );
        setActive([index]);
      };

      // Swiping the carousel moves slides in and out of view without the page
      // scroller hearing anything about it.
      const onStripScroll = () => {
        if (window.matchMedia(NARROW_HERO).matches) setActive(visibleIndices());
      };

      hero.addEventListener('scroll', onStripScroll, { passive: true });
      this.pageTeardown.push(() => hero.removeEventListener('scroll', onStripScroll));

      setActive(window.matchMedia(NARROW_HERO).matches ? visibleIndices() : [0]);
      this.scroll.on('scroll', onHeroScroll);

      this.pageTeardown.push(() => {
        this.scroll.off('scroll', onHeroScroll);
        frames.forEach((frame) => command(frame, 'pause'));
      });
    }

    /**
     * Dots for the hero carousel. Under NARROW_HERO the banners sit in a
     * horizontal scroll-snap strip rather than stacking (see _home.scss);
     * without something marking position, a visitor has no way of knowing
     * there is more than one banner.
     *
     * Built here rather than in the template because they are only meaningful
     * once the strip can be scrolled: with no JS the strip still swipes, it
     * just goes unannounced.
     */
    initHeroCarousel() {
      const hero = document.querySelector('#intro');
      if (!hero) return;

      const slides = [...hero.querySelectorAll('.introSlide')];
      if (slides.length < 2) return;

      const offsetOf = (slide) =>
        slide.getBoundingClientRect().left - hero.getBoundingClientRect().left + hero.scrollLeft;

      const dots = document.createElement('div');
      dots.className = 'hero-dots';

      const buttons = slides.map((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show banner ${i + 1} of ${slides.length}`);
        dot.addEventListener('click', () => {
          hero.scrollTo({ left: offsetOf(slide), behavior: 'smooth' });
        });
        dots.appendChild(dot);
        return dot;
      });

      hero.parentNode.insertBefore(dots, hero.nextSibling);

      const syncDots = () => {
        const middle = hero.scrollLeft + hero.clientWidth / 2;
        let current = 0;
        let nearest = Infinity;

        slides.forEach((slide, i) => {
          const distance = Math.abs(offsetOf(slide) + slide.offsetWidth / 2 - middle);
          if (distance < nearest) {
            nearest = distance;
            current = i;
          }
        });

        buttons.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
      };

      // Locomotive's virtual scroll is still running between 680px and 1024px
      // and would swallow a sideways swipe before the strip ever sees it.
      const keepGesture = (event) => {
        if (window.matchMedia(NARROW_HERO).matches) event.stopPropagation();
      };

      const gestures = ['touchstart', 'touchmove', 'wheel'];
      gestures.forEach((type) => hero.addEventListener(type, keepGesture, { passive: true }));
      hero.addEventListener('scroll', syncDots, { passive: true });
      syncDots();

      this.pageTeardown.push(() => {
        gestures.forEach((type) => hero.removeEventListener(type, keepGesture));
        hero.removeEventListener('scroll', syncDots);
        dots.remove();
      });
    }

    hidePreloader() {
      if (!this.preloader) return;
      this.preloader.style.left = 0;
      this.preloader.style.right = '100vw';
    }

    initPage() {
      this.leftPanel = document.querySelector('.gallery__leftPanel');
      this.nav = document.querySelector('.navigation');
      this.homeSlide = document.querySelector('.homeslider');

      this.replaceEmail();
      this.initAudioToggle();
      this.initVideoPassthrough();
      this.initHeroVideos();
      this.initHeroCarousel();
      this.setHomeSlideHeight();

      // Hide the preloader as soon as images settle, with a hard cap so a slow
      // or broken image can never leave it stuck over the page.
      const preloaderTimer = setTimeout(() => this.hidePreloader(), PRELOADER_MAX_WAIT);
      this.pageTeardown.push(() => clearTimeout(preloaderTimer));

      const container = document.querySelector('[data-site="container"]') || document.body;

      imagesLoaded(container, () => {
        clearTimeout(preloaderTimer);
        this.hidePreloader();
        this.resizeHandler();
      });

      this.splitScreen = new SplitScreen({
          scroll: this.scroll,
          dragHandle: document.querySelector('.gallery__drag'),
          leftPanel: this.leftPanel,
          rightPanel: document.querySelector('.gallery__rightPanel'),
      });

      this.gallery = new Gallery({
        navigation: this.navigation,
        scroll: this.scroll,
        update: () => {
          this.gallery && this.gallery.setTextOverflowHeight();
          this.splitScreen && this.splitScreen.setGalleryHeight();
        }
      });

      this.navigation.attachGallery(this.gallery);

      // Every page load and Barba navigation lands at the top, so the nav
      // starts visible. This used to be restored only by the first scroll
      // event, which never arrived if scrolling was in a broken state.
      this.navigationVisible = true;
      this.menuButton && this.menuButton.classList.add('mask');
      store.dispatch({ type: "TOGGLE_NAVIGATION_VISABILITY", payload: true });

      // Keep references so these can be detached again in destroyPage().
      // Previously they were re-registered on every navigation and never
      // removed, so each scroll event fanned out across every past page.
      const onScroll = (args) => {
        if (!this.menuButton) return;

        const visible = args.scroll.y < NAV_SCROLL_THRESHOLD;
        if (visible === this.navigationVisible) return;
        this.navigationVisible = visible;

        this.menuButton.classList.toggle('mask', visible);
        store.dispatch({ type: "TOGGLE_NAVIGATION_VISABILITY", payload: visible });
      };

      const onCall = (func, args) => {
        if (func !== "toggleGallery") return;

        const galleryOpen = document.querySelector('.lg-show');
        args === "enter" && !galleryOpen
          ? this.navigation.showGalleryMenu()
          : this.navigation.hideGalleryMenu();
      };

      this.scroll.on('scroll', onScroll);
      this.scroll.on('call', onCall);

      const splitScreen = this.splitScreen;
      const gallery = this.gallery;

      this.pageTeardown.push(() => {
        this.scroll.off('scroll', onScroll);
        this.scroll.off('call', onCall);
        this.navigationVisible = null;
        gallery.destroy();
        splitScreen.destroy();
      });
    }

    setupWindowEvents() {
        this.onResize = () => {
          // Coalesce resize bursts (mobile address-bar show/hide fires these
          // continuously) into a single update per frame.
          if (this.resizeFrame) return;

          this.resizeFrame = window.requestAnimationFrame(() => {
            this.resizeFrame = null;
            this.resizeHandler();
          });
        };

        window.addEventListener('resize', this.onResize);

        // Pause smooth scroll only when the tab is genuinely hidden.
        // This previously listened for window blur/focus, but focusing any
        // embedded Vimeo iframe fires blur on the top window — which stopped
        // scrolling with no matching focus event to start it again.
        document.addEventListener('visibilitychange', () => {
          document.hidden ? this.scroll.stop() : this.scroll.start();
        });
    }

    resizeHandler() {
      const context = this.getScrollContext();
      const wasSmooth = this.isSmoothContext(this.scrollContext);
      const isSmooth = this.isSmoothContext(context);

      // Set before rebuilding: initPage() calls back into resizeHandler().
      this.scrollContext = context;

      if (wasSmooth !== isSmooth) {
        this.rebuildScroll();
        return;
      }

      this.setHomeSlideHeight();
      if (this.scroll) this.scroll.update();
      if (this.gallery) this.gallery.update();
      if (this.splitScreen) this.splitScreen.update();
    }
}

export default Site;
